import React from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Typography, 
  Button, 
  Skeleton,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';

/**
 * RecentCustomers Component
 * Displays a card with the most recent customers
 */
const RecentCustomers = ({ customers = [], loading = false }) => {
  const navigate = useNavigate();
  
  // Get initials from customer first and last name
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Get flag emoji based on country code
  const getCountryDisplay = (country) => {
    if (!country) return 'Unknown';
    
    // Format country name
    const countryName = country === 'usa' 
      ? 'United States'
      : country.charAt(0).toUpperCase() + country.slice(1);
    
    return countryName;
  };
  
  return (
    <Card>
      <CardHeader 
        title="Recent Customers" 
        action={
          <Button 
            color="primary" 
            onClick={() => navigate('/admin/customers')}
            disabled={loading}
          >
            View All
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <List>
            {[1, 2, 3].map((_, index) => (
              <ListItem key={index} divider={index < 2}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
                <Skeleton width={80} />
              </ListItem>
            ))}
          </List>
        ) : customers.length === 0 ? (
          <List>
            <ListItem>
              <ListItemText 
                primary="No recent customers" 
                secondary="New customers will appear here when they sign up"
              />
            </ListItem>
          </List>
        ) : (
          <List>
            {customers.map((customer, index) => (
              <ListItem
                key={customer._id || index}
                divider={index < customers.length - 1}
                button
                onClick={() => navigate(`/admin/customers/${customer._id}`)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {getInitials(customer.firstName, customer.lastName)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown Customer'}
                  secondary={
                    <>
                      {customer.email}
                      {customer.createdAt && (
                        <Typography 
                          component="span" 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {formatDate(customer.createdAt)}
                        </Typography>
                      )}
                    </>
                  }
                />
                {customer.country && (
                  <Chip 
                    label={getCountryDisplay(customer.country)} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentCustomers;